import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FieldValues } from 'react-hook-form';
import {
  getProcessExecutionErrorMessage,
  isAuthError,
  isCamundaRequestError,
  isConcurrentTaskUpdateError,
  isProcessExecutionError,
  isTaskNotActiveError,
} from '@/lib/camunda/base-service';
import { CamundaFormsService } from '@/lib/camunda/forms-service';
import {
  loadTaskFormComponent,
  loadTaskSuccessComponent,
  loadFormSchema,
  loadFormPreloadData,
  getDefaultValuesFromSchema,
  getAvailableForms
} from '@/lib/form-utilities';
import { resolveFormKey } from '@/lib/process-registry';
import { loadHistoricFormData } from '@/lib/historic-form-loader';
import { isHistoricFileReference } from '@/lib/camunda/form-file-values';
import { normalizeRiskDraftValues } from '@/forms/comercial/aprobacion-riesgos/alta-riesgos/revenue-format';
import { createBpmEventId, trackActionFailed, trackActionStarted, trackActionSucceeded } from '@/lib/telemetry/bpm-telemetry';
import { useTaskStore } from './task-store';
import type {
  FormActionResult,
  FormDraftPersistenceResult,
  FormStore,
} from './form-store.types';
import type {
  CamundaFormObject,
  CamundaFormValue,
  CamundaFormValues,
  CamundaTask,
} from '@/lib/camunda/types';
import type { HistoricFileReference } from '@/lib/camunda/form-file-values';

type FormTelemetryAction = 'save' | 'send';

const formatActionError = (action: 'guardar' | 'enviar', error: Error) => {
  if (isTaskNotActiveError(error)) {
    return 'Esta tarea ya no esta activa. Puede que ya se haya enviado, reasignado o reemplazado por una nueva tarea.';
  }
  if (isAuthError(error)) {
    return 'Tu sesion no esta autorizada o ha caducado. Vuelve a iniciar sesion e intenta de nuevo.';
  }
  if (isConcurrentTaskUpdateError(error)) {
    return 'La tarea cambio en Camunda mientras intentabas completar la accion. Hemos refrescado el estado para evitar trabajar sobre datos obsoletos.';
  }
  if (isProcessExecutionError(error)) {
    return getProcessExecutionErrorMessage(error);
  }
  const detail = error instanceof Error ? error.message : 'Error desconocido';
  return `No se pudo ${action} el formulario. La tarea sigue abierta y puedes intentarlo de nuevo. Detalle: ${detail}`;
};

const formatLoadFormError = (error: Error) => {
  if (isTaskNotActiveError(error)) {
    return 'Esta tarea ya no esta activa. Puede que ya se haya enviado, reasignado o reemplazado por una nueva tarea.';
  }
  if (isAuthError(error)) {
    return 'Tu sesion no esta autorizada o ha caducado. Vuelve a iniciar sesion e intenta de nuevo.';
  }
  return error instanceof Error ? error.message : 'Error al cargar el formulario';
};

const actionResultFromError = (error: Error, message: string, eventId?: string): FormActionResult => {
  if (isTaskNotActiveError(error)) {
    return { ok: false, reason: 'task_not_active', message, eventId };
  }
  if (isAuthError(error)) {
    return { ok: false, reason: 'auth_error', message, eventId };
  }
  if (isConcurrentTaskUpdateError(error)) {
    return { ok: false, reason: 'conflict', message, eventId };
  }
  if (isProcessExecutionError(error)) {
    return { ok: false, reason: 'process_error', message, eventId };
  }
  return { ok: false, reason: 'error', message, eventId };
};

const draftPersistenceResultFromError = (
  error: Error,
): FormDraftPersistenceResult => {
  const message = formatActionError('guardar', error);
  if (isTaskNotActiveError(error)) {
    return { ok: false, reason: 'task_not_active', message };
  }
  if (isAuthError(error)) {
    return { ok: false, reason: 'auth_error', message };
  }
  if (isConcurrentTaskUpdateError(error)) {
    return { ok: false, reason: 'conflict', message };
  }
  return { ok: false, reason: 'error', message };
};

function getTaskForTelemetry(taskId: string): CamundaTask | null {
  const { tasks, currentTask } = useTaskStore.getState();
  return tasks.find(task => task.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
}

type FormVariableSpecialSnapshot =
  | {
      kind: 'file';
      value: File;
      name: string;
      type: string;
      size: number;
    }
  | {
      kind: 'blob';
      value: Blob;
      type: string;
      size: number;
    }
  | {
      kind: 'historic-file';
      historicDetailId: string;
      filename: string;
      mimeType: string | null;
      processInstanceId: string;
      taskId: string;
    };

interface FormVariableRecord {
  readonly [key: string]: FormVariableValue;
}

type FormVariableValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | File
  | Blob
  | HistoricFileReference
  | FormVariableRecord
  | FormVariableValue[];

const formVariableBlobBytes = new WeakMap<Blob, Promise<Uint8Array | null>>();

function isFileSnapshotValue(value: FormVariableValue): value is File {
  if (globalThis.File !== undefined && value instanceof globalThis.File) {
    return true;
  }
  return Object.prototype.toString.call(value) === '[object File]';
}

function isBlobSnapshotValue(value: FormVariableValue): value is Blob {
  if (globalThis.Blob !== undefined && value instanceof globalThis.Blob) {
    return true;
  }
  return Object.prototype.toString.call(value) === '[object Blob]';
}

async function readFormVariableBlobBytes(value: Blob): Promise<Uint8Array | null> {
  try {
    return new Uint8Array(await value.arrayBuffer());
  } catch {
    return null;
  }
}

function getFormVariableBlobBytes(value: Blob): Promise<Uint8Array | null> {
  const cached = formVariableBlobBytes.get(value);
  if (cached) {
    return cached;
  }
  const reading = readFormVariableBlobBytes(value);
  formVariableBlobBytes.set(value, reading);
  return reading;
}

async function areFormVariableBlobBytesEqual(left: Blob, right: Blob): Promise<boolean> {
  const [leftBytes, rightBytes] = await Promise.all([
    getFormVariableBlobBytes(left),
    getFormVariableBlobBytes(right),
  ]);
  if (!leftBytes || !rightBytes || leftBytes.byteLength !== rightBytes.byteLength) {
    return false;
  }
  for (let index = 0; index < leftBytes.byteLength; index += 1) {
    if (leftBytes[index] !== rightBytes[index]) {
      return false;
    }
  }
  return true;
}

function getFormVariableSpecialSnapshot(value: FormVariableValue): FormVariableSpecialSnapshot | null {
  if (isFileSnapshotValue(value)) {
    return {
      kind: 'file',
      value,
      name: value.name,
      type: value.type,
      size: value.size,
    };
  }
  if (isBlobSnapshotValue(value)) {
    return {
      kind: 'blob',
      value,
      type: value.type,
      size: value.size,
    };
  }
  if (isHistoricFileReference(value)) {
    return {
      kind: 'historic-file',
      historicDetailId: value.historicDetailId,
      filename: value.filename,
      mimeType: value.mimeType,
      processInstanceId: value.processInstanceId,
      taskId: value.taskId,
    };
  }
  return null;
}

async function areSpecialFormVariableSnapshotsEqual(
  left: FormVariableSpecialSnapshot,
  right: FormVariableSpecialSnapshot,
): Promise<boolean> {
  if (left.kind !== right.kind) {
    return false;
  }
  if (left.kind === 'file' && right.kind === 'file') {
    const metadataMatches = (
      left.name === right.name
      && left.type === right.type
      && left.size === right.size
    );
    return metadataMatches && areFormVariableBlobBytesEqual(left.value, right.value);
  }
  if (left.kind === 'blob' && right.kind === 'blob') {
    const metadataMatches = left.type === right.type && left.size === right.size;
    return metadataMatches && areFormVariableBlobBytesEqual(left.value, right.value);
  }
  if (left.kind === 'historic-file' && right.kind === 'historic-file') {
    return (
      left.historicDetailId === right.historicDetailId
      && left.filename === right.filename
      && left.mimeType === right.mimeType
      && left.processInstanceId === right.processInstanceId
      && left.taskId === right.taskId
    );
  }
  return false;
}

function isFormVariableObject(value: FormVariableValue): value is FormVariableRecord {
  return Object.prototype.toString.call(value) === '[object Object]';
}

type CamundaValueType =
  | 'Boolean'
  | 'Bytes'
  | 'Date'
  | 'Double'
  | 'File'
  | 'Integer'
  | 'Json'
  | 'Long'
  | 'Null'
  | 'Object'
  | 'Short'
  | 'String';

type CamundaValueWrapper = CamundaFormObject & {
  readonly value: CamundaFormValue;
  readonly type: CamundaValueType;
};

function isCamundaFormObject(value: CamundaFormValue): value is CamundaFormObject {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function isCamundaValueWrapper(value: CamundaFormValue): value is CamundaValueWrapper {
  return (
    isCamundaFormObject(value)
    && Object.hasOwn(value, 'value')
    && Object.hasOwn(value, 'type')
    && (
      value.type === 'Boolean'
      || value.type === 'Bytes'
      || value.type === 'Date'
      || value.type === 'Double'
      || value.type === 'File'
      || value.type === 'Integer'
      || value.type === 'Json'
      || value.type === 'Long'
      || value.type === 'Null'
      || value.type === 'Object'
      || value.type === 'Short'
      || value.type === 'String'
    )
  );
}

function normalizeTaskFormVariables(
  variables: CamundaFormValues | null | undefined,
): FieldValues {
  if (variables === null || variables === undefined) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(variables).map(([name, value]) => [
      name,
      isCamundaValueWrapper(value) ? value.value : value,
    ]),
  );
}

async function areFormVariableSnapshotsEqual(
  left: FieldValues | FormVariableValue,
  right: FieldValues | FormVariableValue,
): Promise<boolean> {
  if (Object.is(left, right)) {
    return true;
  }
  const leftSpecialSnapshot = getFormVariableSpecialSnapshot(left);
  const rightSpecialSnapshot = getFormVariableSpecialSnapshot(right);
  if (leftSpecialSnapshot || rightSpecialSnapshot) {
    return (
      leftSpecialSnapshot !== null
      && rightSpecialSnapshot !== null
      && await areSpecialFormVariableSnapshotsEqual(leftSpecialSnapshot, rightSpecialSnapshot)
    );
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }
    const comparisons = await Promise.all(
      left.map((value, index) => areFormVariableSnapshotsEqual(value, right[index])),
    );
    return comparisons.every(Boolean);
  }
  if (!isFormVariableObject(left) || !isFormVariableObject(right)) {
    return false;
  }

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  if (!leftKeys.every((key, index) => key === rightKeys[index])) {
    return false;
  }
  const comparisons = await Promise.all(
    leftKeys.map(key => areFormVariableSnapshotsEqual(left[key], right[key])),
  );
  return comparisons.every(Boolean);
}

function cloneFormVariableValue(value: FormVariableValue): FormVariableValue {
  if (isFileSnapshotValue(value) || isBlobSnapshotValue(value)) {
    return value;
  }
  if (isHistoricFileReference(value)) {
    return {
      _camundaType: value._camundaType,
      historicDetailId: value.historicDetailId,
      filename: value.filename,
      mimeType: value.mimeType,
      processInstanceId: value.processInstanceId,
      taskId: value.taskId,
    };
  }
  if (Array.isArray(value)) {
    return value.map(item => cloneFormVariableValue(item));
  }
  if (isFormVariableObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneFormVariableValue(item)]),
    );
  }
  return value;
}

function cloneFormVariableSnapshot(value: FieldValues): FieldValues {
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, cloneFormVariableValue(item)]),
  );
}

function buildBpmTelemetryContext(taskId: string, formKey?: string | null) {
  const task = getTaskForTelemetry(taskId);
  return {
    taskId,
    processInstanceId: task?.processInstanceId,
    processDefinitionId: task?.processDefinitionId,
    processKey: task?.processDefinitionKey,
    taskDefinitionKey: task?.taskDefinitionKey,
    formKey: formKey ?? task?.formKey ?? undefined,
    assignee: task?.assignee ?? undefined,
  };
}

function getSafeFormMetadata(data: FieldValues) {
  const fieldNames = Object.keys(data);
  let fileCount = 0;
  const fileFieldNames: string[] = [];

  for (const [fieldName, value] of Object.entries(data)) {
    const fileConstructor = globalThis.File;
    if (fileConstructor && value instanceof fileConstructor) {
      fileCount += 1;
      fileFieldNames.push(fieldName);
      continue;
    }
    const fileListConstructor = globalThis.FileList;
    if (fileListConstructor && value instanceof fileListConstructor && value.length > 0) {
      fileCount += value.length;
      fileFieldNames.push(fieldName);
    }
  }

  return {
    fieldNames,
    fieldCount: fieldNames.length,
    fileFieldNames: fileFieldNames.length > 0 ? fileFieldNames : undefined,
    fileCount: fileCount > 0 ? fileCount : undefined,
  };
}

function getTelemetryNow(): number {
  return globalThis.performance?.now?.() ?? Date.now();
}

function getActionDurationMs(startedAt: number): number {
  return Math.max(0, Math.round(getTelemetryNow() - startedAt));
}

function getCamundaHttpContext(error: Error) {
  if (!isCamundaRequestError(error)) {
    return undefined;
  }

  return {
    endpoint: error.endpoint.split('?')[0] || error.endpoint,
    status: error.status,
    statusText: error.statusText,
  };
}

function trackFormActionStarted(action: FormTelemetryAction, eventId: string, taskId: string, formKey: string | undefined, data: FieldValues): number {
  const startedAt = getTelemetryNow();
  trackActionStarted({
    eventId,
    action,
    bpm: buildBpmTelemetryContext(taskId, formKey),
    form: getSafeFormMetadata(data),
  });
  return startedAt;
}

export const useFormStore = create<FormStore>()(devtools(
  (set, get) => ({
    isLoading: false,
    isSaving: false,
    isSubmitting: false,
    error: null,
    isHistoricView: false,
    actionError: null,

    formInfo: null,
    loadedTaskId: null,
    formRequestGeneration: 0,
    variables: {},
    appliedRemoteVariables: {},
    observedRemoteVariables: {},
    preloadedData: null,

    FormComponent: null,
    SuccessComponent: null,

    schema: null,

    submissionState: 'form',
    submittedData: null,

    availableForms: [],

    loadForm: async (taskId: string) => {
      const requestGeneration = get().formRequestGeneration + 1;
      set({
        isLoading: true,
        isSaving: false,
        isSubmitting: false,
        error: null,
        actionError: null,
        loadedTaskId: null,
        formRequestGeneration: requestGeneration,
      });

      try {
        const formInfo = await CamundaFormsService.getTaskForm(taskId);

        if (!formInfo) {
          throw new Error('No se pudo obtener la información del formulario');
        }

        if (get().formRequestGeneration !== requestGeneration) {
          return;
        }

        const availableForms = getAvailableForms();
        const resolvedFormKey = resolveFormKey(formInfo.key, availableForms);

        if (!resolvedFormKey) {
          throw new Error(`Formulario '${formInfo.key}' no encontrado en camunda-forms`);
        }

        if (get().formRequestGeneration !== requestGeneration) {
          return;
        }

        const normalizedFormInfo = { ...formInfo, key: resolvedFormKey };

        const [
          FormComponent,
          SuccessComponent,
          schema,
          variables,
          preloadResult,
        ] = await Promise.all([
          loadTaskFormComponent(resolvedFormKey),
          loadTaskSuccessComponent(resolvedFormKey),
          loadFormSchema(resolvedFormKey),
          CamundaFormsService.getOptimizedTaskFormVariables(taskId, resolvedFormKey, availableForms)
            .then(normalizeTaskFormVariables),
          loadFormPreloadData(resolvedFormKey),
        ]);

        if (!schema) {
          throw new Error('No se pudo cargar el schema del formulario');
        }

        const defaultValues = getDefaultValuesFromSchema(schema);
        const mergedVariables = {
          ...defaultValues,
          ...preloadResult.formValues,
          ...variables,
        };

        if (get().formRequestGeneration !== requestGeneration) {
          return;
        }

        set({
          isLoading: false,
          formInfo: normalizedFormInfo,
          loadedTaskId: taskId,
          availableForms,
          FormComponent,
          SuccessComponent,
          schema,
          variables: mergedVariables,
          appliedRemoteVariables: cloneFormVariableSnapshot(variables),
          observedRemoteVariables: cloneFormVariableSnapshot(variables),
          preloadedData: preloadResult.data,
          submissionState: 'form',
          isHistoricView: false,
          error: null,
          actionError: null,
        });

      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        if (get().formRequestGeneration !== requestGeneration) {
          return;
        }
        console.error('Error loading form:', normalizedError);
        const loadError = formatLoadFormError(normalizedError);
        set({
          isLoading: false,
          error: loadError,
          formInfo: null,
          loadedTaskId: null,
          FormComponent: null,
          SuccessComponent: null,
          schema: null,
          variables: {},
          appliedRemoteVariables: {},
          observedRemoteVariables: {},
          preloadedData: null,
          isHistoricView: false,
        });
      }
    },

    inspectRemoteFormChange: async (taskId: string) => {
      const {
        formInfo,
        loadedTaskId,
        formRequestGeneration,
        availableForms,
      } = get();
      if (!formInfo || loadedTaskId !== taskId) {
        throw new Error('El formulario activo no corresponde con la tarea que se intenta reconciliar');
      }

      const remoteVariables = normalizeTaskFormVariables(
        await CamundaFormsService.getOptimizedTaskFormVariables(
          taskId,
          formInfo.key,
          availableForms,
        ),
      );

      const currentState = get();
      if (
        currentState.formRequestGeneration !== formRequestGeneration
        || currentState.loadedTaskId !== taskId
        || currentState.formInfo?.key !== formInfo.key
      ) {
        return { changed: false, newlyObserved: false };
      }

      const changed = !(await areFormVariableSnapshotsEqual(
        remoteVariables,
        currentState.appliedRemoteVariables,
      ));
      const newlyObserved = changed
        && !(await areFormVariableSnapshotsEqual(
          remoteVariables,
          currentState.observedRemoteVariables,
        ));
      const latestState = get();
      if (
        latestState.formRequestGeneration !== formRequestGeneration
        || latestState.loadedTaskId !== taskId
        || latestState.formInfo?.key !== formInfo.key
        || latestState.appliedRemoteVariables !== currentState.appliedRemoteVariables
        || latestState.observedRemoteVariables !== currentState.observedRemoteVariables
      ) {
        return { changed: false, newlyObserved: false };
      }
      set({ observedRemoteVariables: cloneFormVariableSnapshot(remoteVariables) });
      return { changed, newlyObserved };
    },

    loadHistoricTaskForm: async (task, processInstanceId, formKey) => {
      const requestGeneration = get().formRequestGeneration + 1;
      set({
        isLoading: true,
        isSaving: false,
        isSubmitting: false,
        error: null,
        isHistoricView: true,
        loadedTaskId: null,
        formRequestGeneration: requestGeneration,
      });

      try {
        const {
          formKey: resolvedFormKey,
          FormComponent,
          schema,
          variables,
          preloadedData,
        } = await loadHistoricFormData(task, processInstanceId, formKey);

        if (get().formRequestGeneration !== requestGeneration) {
          return;
        }

        set({
          isLoading: false,
          formInfo: { key: resolvedFormKey },
          loadedTaskId: null,
          availableForms: getAvailableForms(),
          FormComponent,
          SuccessComponent: null,
          schema,
          variables,
          appliedRemoteVariables: cloneFormVariableSnapshot(variables),
          observedRemoteVariables: cloneFormVariableSnapshot(variables),
          preloadedData,
          submissionState: 'form',
          isHistoricView: true,
          error: null,
        });
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        if (get().formRequestGeneration !== requestGeneration) {
          return;
        }
        console.error('Error loading historic task form:', normalizedError);
        set({
          isLoading: false,
          error: normalizedError.message || 'Error al cargar el formulario histórico',
          formInfo: null,
          loadedTaskId: null,
          FormComponent: null,
          SuccessComponent: null,
          schema: null,
          variables: {},
          appliedRemoteVariables: {},
          observedRemoteVariables: {},
          preloadedData: null,
          isHistoricView: true,
        });
      }
    },

    saveForm: async (taskId: string, data: FieldValues) => {
      const requestGeneration = get().formRequestGeneration;
      set({ isSaving: true, actionError: null });
      const eventId = createBpmEventId();
      let startedAt = getTelemetryNow();

      try {
        const { formInfo } = get();
        const formKey = formInfo?.key;
        const draftData = normalizeRiskDraftValues(formKey, data);
        startedAt = trackFormActionStarted('save', eventId, taskId, formKey, data);

        const success = await CamundaFormsService.saveTaskFormVariables(taskId, draftData, formKey);

        if (success) {
          trackActionSucceeded({
            eventId,
            action: 'save',
            bpm: buildBpmTelemetryContext(taskId, formKey),
            form: getSafeFormMetadata(data),
            durationMs: getActionDurationMs(startedAt),
          });
          const currentState = get();
          if (
            currentState.formRequestGeneration === requestGeneration
            && currentState.loadedTaskId === taskId
          ) {
            const appliedRemoteVariables = cloneFormVariableSnapshot({
              ...currentState.appliedRemoteVariables,
              ...draftData,
            });
            set({
              isSaving: false,
              variables: { ...currentState.variables, ...draftData },
              appliedRemoteVariables,
              observedRemoteVariables: cloneFormVariableSnapshot(appliedRemoteVariables),
              actionError: null
            });
          }
        } else {
          trackActionFailed({
            eventId,
            action: 'save',
            bpm: buildBpmTelemetryContext(taskId, formKey),
            form: getSafeFormMetadata(data),
            durationMs: getActionDurationMs(startedAt),
            error: 'CamundaFormsService.saveTaskFormVariables returned false',
          });
          const currentState = get();
          if (
            currentState.formRequestGeneration === requestGeneration
            && currentState.loadedTaskId === taskId
          ) {
            set({
              isSaving: false,
              actionError: 'No se pudo guardar el formulario. La tarea sigue abierta y puedes intentarlo de nuevo.'
            });
          }
        }

        return success
          ? { ok: true }
          : {
              ok: false,
              reason: 'error',
              message: 'No se pudo guardar el formulario. La tarea sigue abierta y puedes intentarlo de nuevo.',
              eventId,
            };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        const { formInfo } = get();
        const formKey = formInfo?.key;
        const actionError = formatActionError('guardar', normalizedError);
        trackActionFailed({
          eventId,
          action: 'save',
          bpm: buildBpmTelemetryContext(taskId, formKey),
          form: getSafeFormMetadata(data),
          durationMs: getActionDurationMs(startedAt),
          http: getCamundaHttpContext(normalizedError),
          error: normalizedError.message,
        });
        const currentState = get();
        if (
          currentState.formRequestGeneration === requestGeneration
          && currentState.loadedTaskId === taskId
        ) {
          set({
            isSaving: false,
            actionError
          });
        }
        return actionResultFromError(normalizedError, actionError, eventId);
      }
    },

    persistTaskFormDraft: async (taskId: string, data: FieldValues, source: 'input' | 'validated') => {
      try {
        const formKey = get().formInfo?.key;
        const draftData = source === 'input' ? normalizeRiskDraftValues(formKey, data) : data;
        await CamundaFormsService.persistTaskFormDraft(taskId, draftData, formKey);
        return { ok: true };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        return draftPersistenceResultFromError(normalizedError);
      }
    },

    submitForm: async (taskId: string, data: FieldValues) => {
      const requestGeneration = get().formRequestGeneration;
      set({ isSubmitting: true, actionError: null });
      const eventId = createBpmEventId();
      let startedAt = getTelemetryNow();

      try {
        const { formInfo } = get();
        const formKey = formInfo?.key;
        startedAt = trackFormActionStarted('send', eventId, taskId, formKey, data);

        const success = await CamundaFormsService.completeTaskWithFormData(taskId, data, formKey);
        const currentState = get();
        const requestIsCurrent = (
          currentState.formRequestGeneration === requestGeneration
          && currentState.loadedTaskId === taskId
        );

        if (success) {
          trackActionSucceeded({
            eventId,
            action: 'send',
            bpm: buildBpmTelemetryContext(taskId, formKey),
            form: getSafeFormMetadata(data),
            durationMs: getActionDurationMs(startedAt),
          });
          if (requestIsCurrent) {
            set({
              isSubmitting: false,
              submissionState: 'success',
              submittedData: data,
              actionError: null
            });
          }
        } else {
          trackActionFailed({
            eventId,
            action: 'send',
            bpm: buildBpmTelemetryContext(taskId, formKey),
            form: getSafeFormMetadata(data),
            durationMs: getActionDurationMs(startedAt),
            error: 'CamundaFormsService.completeTaskWithFormData returned false',
          });
          if (requestIsCurrent) {
            set({
              isSubmitting: false,
              actionError: 'No se pudo enviar el formulario. La tarea sigue abierta y puedes intentarlo de nuevo.'
            });
          }
        }

        if (!requestIsCurrent) {
          return {
            ok: false,
            reason: 'stale',
            message: 'La respuesta pertenece a una tarea que ya no esta abierta.',
            eventId,
          };
        }

        return success
          ? { ok: true }
          : {
              ok: false,
              reason: 'error',
              message: 'No se pudo enviar el formulario. La tarea sigue abierta y puedes intentarlo de nuevo.',
              eventId,
            };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        const { formInfo } = get();
        const formKey = formInfo?.key;
        const actionError = formatActionError('enviar', normalizedError);
        trackActionFailed({
          eventId,
          action: 'send',
          bpm: buildBpmTelemetryContext(taskId, formKey),
          form: getSafeFormMetadata(data),
          durationMs: getActionDurationMs(startedAt),
          http: getCamundaHttpContext(normalizedError),
          error: normalizedError.message,
        });
        const currentState = get();
        const requestIsCurrent = (
          currentState.formRequestGeneration === requestGeneration
          && currentState.loadedTaskId === taskId
        );
        if (requestIsCurrent) {
          set({
            isSubmitting: false,
            actionError
          });
        }
        if (!requestIsCurrent) {
          return {
            ok: false,
            reason: 'stale',
            message: 'La respuesta pertenece a una tarea que ya no esta abierta.',
            eventId,
          };
        }
        return actionResultFromError(normalizedError, actionError, eventId);
      }
    },

    clearForm: () => {
      set((state) => ({
        isLoading: false,
        isSaving: false,
        isSubmitting: false,
        error: null,
        actionError: null,
        formInfo: null,
        loadedTaskId: null,
        formRequestGeneration: state.formRequestGeneration + 1,
        variables: {},
        appliedRemoteVariables: {},
        observedRemoteVariables: {},
        preloadedData: null,
        FormComponent: null,
        SuccessComponent: null,
        schema: null,
        submissionState: 'form',
        submittedData: null,
        availableForms: [],
        isHistoricView: false,
      }));
    },

    clearError: () => {
      set({ error: null });
    },

    clearActionError: () => {
      set({ actionError: null });
    },

    setSubmissionState: (state: 'form' | 'success') => {
      set({ submissionState: state });
    },

    resetToForm: () => {
      set({
        submissionState: 'form',
        submittedData: null
      });
    }
  }),
  {
    name: 'form-store'
  }
));

export const useFormLoading = () => useFormStore(state => state.isLoading);
export const useFormSaving = () => useFormStore(state => state.isSaving);
export const useFormSubmitting = () => useFormStore(state => state.isSubmitting);
export const useFormError = () => useFormStore(state => state.error);
export const useFormInfo = () => useFormStore(state => state.formInfo);
export const useFormVariables = () => useFormStore(state => state.variables);
export const useFormPreloadedData = () => useFormStore(state => state.preloadedData);
export const useFormComponent = () => useFormStore(state => state.FormComponent);
export const useSuccessComponent = () => useFormStore(state => state.SuccessComponent);
export const useFormSchema = () => useFormStore(state => state.schema);
export const useSubmissionState = () => useFormStore(state => state.submissionState);
export const useSubmittedData = () => useFormStore(state => state.submittedData);

export const useLoadForm = () => useFormStore(state => state.loadForm);
export const useInspectRemoteFormChange = () => useFormStore(state => state.inspectRemoteFormChange);
export const useSaveForm = () => useFormStore(state => state.saveForm);
export const useSubmitForm = () => useFormStore(state => state.submitForm);
export const usePersistTaskFormDraft = () => useFormStore(state => state.persistTaskFormDraft);
export const useClearForm = () => useFormStore(state => state.clearForm);
export const useClearFormActionError = () => useFormStore(state => state.clearActionError);
